import { useEffect, useMemo, useRef } from 'react'

import { CanvasConfigV2 } from '@editor/interfaces/editor'
import { BlueprintEditorTools } from '@editor/pages/projects/blueprint-editor/definitions'
import { setEditorElements } from '@editor/pages/projects/blueprint-editor/store/drawings'
import usePlaneDetectionMainCanvas from '@editor/pages/projects/editor/tools/PlaneDetection/hooks/useMainCanvas'
import usePolylineMainCanvas from '@editor/pages/projects/editor/tools/Polyline/hooks/Polyline/useMainCanvas'
import useVolumeEstimationMainCanvas from '@editor/pages/projects/editor/tools/VolumeEstimation/Polygon/hooks/useMainCanvas'
import { useAppDispatch, useAppSelector } from '@editor/store/app'

import useAutoTextAnnotation from './useAutoTextAnnotation'
import useObjects from './useObjects'

const useMainCanvas = (): CanvasConfigV2 => {
  // Store
  const dispatch = useAppDispatch()
  const selectedBlueprint = useAppSelector((state) => state.blueprint.editor.selectedBlueprint)
  const isCreatingBlueprint = useAppSelector((state) => state.blueprint.editor.isCreatingBlueprint)

  // Vars
  const isTextAnnotationToolSelected = useAppSelector(
    (state) => state.blueprint.editor.selectedTool === BlueprintEditorTools.TextAnnotation,
  )

  // Refs
  const initializedBlueprintIdRef = useRef<string | null>(null)

  // Get all shapes
  const useMainCanvasProps = {
    pointClouds: [],
    showLabels: !isTextAnnotationToolSelected,
  }
  const polyline = usePolylineMainCanvas(useMainCanvasProps)
  const planeDetection = usePlaneDetectionMainCanvas(useMainCanvasProps)
  const volumeEstimation = useVolumeEstimationMainCanvas(useMainCanvasProps)

  // Hooks
  const { objects } = useObjects(polyline, planeDetection, volumeEstimation)
  const { objects: autoTextAnnotationObjects } = useAutoTextAnnotation(polyline, planeDetection, volumeEstimation)

  // Combine all objects
  const allObjects = useMemo(
    () => ({
      ...objects,
      ...autoTextAnnotationObjects,
    }),
    [objects, autoTextAnnotationObjects],
  )

  /**
   * Set checked elements for the selected blueprint.
   */
  useEffect(() => {
    if (!selectedBlueprint?.sort_key || initializedBlueprintIdRef.current === selectedBlueprint.blueprint_id) return

    dispatch(
      setEditorElements({
        inspectionItems: selectedBlueprint.inspection_items || [],
        polygons: selectedBlueprint.polygons || [],
        cylinders: selectedBlueprint.cylinders || [],
      }),
    )

    initializedBlueprintIdRef.current = selectedBlueprint.blueprint_id
  }, [selectedBlueprint, dispatch])

  /**
   * When starting to create a new blueprint, reset the initialized ref.
   */
  useEffect(() => {
    if (isCreatingBlueprint) {
      initializedBlueprintIdRef.current = null
    }
  }, [isCreatingBlueprint])

  return {
    objects: allObjects,
  }
}

export default useMainCanvas
